// @ts-nocheck
/* ============================================================================
   AMOLIH — Wishlist Engine (Phase 2)
   ============================================================================
   Storage:   localStorage key `amolih_wishlist`
              → JSON array of wishlist entries keyed by variant id when
                available, otherwise product id.
   Event:     `amolih:wishlist:change` dispatched on window on every mutation
              detail = { items: [{id, handle}], ids: [id], count: N }
   Global:    window.AmolihWishlist = {
                get, getIds, has, toggle, add, remove, count, _sync
              }

   Migration: Phase 1 stored an array of plain ID strings. This file reads
              that legacy format transparently — it gets converted to the
              new {id, handle} object format on the next write. No data
              loss, no required intervention.
   ============================================================================ */

(function () {
  'use strict';

  var STORAGE_KEY  = 'amolih_wishlist';
  var EVENT_CHANGE = 'amolih:wishlist:change';
  var BTN_SELECTOR = '[data-wishlist-button]';
  var BADGE_SELECTOR = '[data-wishlist-count]';
  var INIT_FLAG    = 'wishlistInit';

  function toStringOrNull(value) {
    return value == null || value === '' ? null : String(value);
  }

  function getEntryKey(item) {
    if (!item) return null;
    if (item.key != null && item.key !== '') return String(item.key);
    if (item.variantId != null && item.variantId !== '') return String(item.variantId);
    if (item.productId != null && item.productId !== '') return String(item.productId);
    if (item.id != null && item.id !== '') return String(item.id);
    return null;
  }

  function parseVariantOptions(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map(function (value) { return String(value); });
    try {
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map(function (value) { return String(value); });
    } catch (e) {
      return [];
    }
  }

  function mergeEntry(existing, incoming) {
    var merged = {};
    var key;
    var i;
    var fields = [
      'key', 'productId', 'variantId', 'handle', 'productTitle', 'variantTitle',
      'variantOptions'
    ];

    for (i = 0; i < fields.length; i++) {
      if (existing && existing[fields[i]] != null) merged[fields[i]] = existing[fields[i]];
    }
    for (i = 0; i < fields.length; i++) {
      if (incoming && incoming[fields[i]] != null) merged[fields[i]] = incoming[fields[i]];
    }

    key = getEntryKey(merged);
    if (key) merged.key = key;
    return merged;
  }

  /* ----------------------------- storage layer ----------------------------- */

  function normalize(item) {
    // Accept either a string (legacy product-level entry) or a rich object.
    if (item == null) return null;
    if (typeof item === 'string' || typeof item === 'number') {
      return { key: String(item), productId: String(item), variantId: null, handle: null };
    }
    if (typeof item === 'object' && item.id != null) {
      var normalized = {
        key: toStringOrNull(item.key),
        productId: toStringOrNull(item.productId != null ? item.productId : item.id),
        variantId: toStringOrNull(item.variantId),
        handle: toStringOrNull(item.handle),
        productTitle: toStringOrNull(item.productTitle),
        variantTitle: toStringOrNull(item.variantTitle),
        title: toStringOrNull(item.title),
        displayTitle: toStringOrNull(item.displayTitle),
        image: toStringOrNull(item.image),
        variantImage: toStringOrNull(item.variantImage),
        price: item.price != null && item.price !== '' ? Number(item.price) : null,
        compareAtPrice: item.compareAtPrice != null && item.compareAtPrice !== '' ? Number(item.compareAtPrice) : null,
        variantOptions: parseVariantOptions(item.variantOptions)
      };

      normalized.key = getEntryKey(normalized);
      if (!normalized.key) return null;
      return normalized;
    }
    if (typeof item === 'object' && (item.productId != null || item.variantId != null || item.key != null)) {
      var rich = {
        key: toStringOrNull(item.key),
        productId: toStringOrNull(item.productId),
        variantId: toStringOrNull(item.variantId),
        handle: toStringOrNull(item.handle),
        productTitle: toStringOrNull(item.productTitle),
        variantTitle: toStringOrNull(item.variantTitle),
        title: toStringOrNull(item.title),
        displayTitle: toStringOrNull(item.displayTitle),
        image: toStringOrNull(item.image),
        variantImage: toStringOrNull(item.variantImage),
        price: item.price != null && item.price !== '' ? Number(item.price) : null,
        compareAtPrice: item.compareAtPrice != null && item.compareAtPrice !== '' ? Number(item.compareAtPrice) : null,
        variantOptions: parseVariantOptions(item.variantOptions)
      };

      rich.key = getEntryKey(rich);
      if (!rich.key) return null;
      return rich;
    }
    return null;
  }

  function safeParse(raw) {
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      var out = [];
      for (var i = 0; i < parsed.length; i++) {
        var n = normalize(parsed[i]);
        if (n) out.push(n);
      }
      return out;
    } catch (e) {
      return [];
    }
  }

  function read() {
    try {
      return safeParse(localStorage.getItem(STORAGE_KEY));
    } catch (e) {
      return [];
    }
  }

  function write(items) {
    var list = [];
    var seen = {};

    (items || []).map(normalize).filter(Boolean).forEach(function (entry) {
      var key = getEntryKey(entry);
      if (!key) return;
      if (seen[key]) {
        seen[key] = mergeEntry(seen[key], entry);
      } else {
        seen[key] = mergeEntry(null, entry);
        list.push(seen[key]);
      }
    });

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      console.warn('[AmolihWishlist] Could not persist:', e);
    }
    var ids = list.map(function (x) { return getEntryKey(x); });
    window.dispatchEvent(new CustomEvent(EVENT_CHANGE, {
      detail: { items: list, ids: ids, count: list.length }
    }));
    return list;
  }

  /* ------------------------------- public API ------------------------------ */

  function get()    { return read(); }
  function getIds() { return read().map(function (x) { return getEntryKey(x); }); }
  function count()  { return read().length; }

  function has(productId) {
    if (productId == null) return false;
    var idStr = String(productId);
    var list = read();
    for (var i = 0; i < list.length; i++) {
      if (getEntryKey(list[i]) === idStr) return true;
    }
    return false;
  }

  function add(productId, productHandle) {
    var incoming = normalize(productId);
    if (!incoming && productId != null) {
      incoming = normalize({ id: productId, handle: productHandle });
    }
    if (!incoming) return read();

    if (productHandle && !incoming.handle) incoming.handle = String(productHandle);

    var list = read();
    var found = false;
    for (var i = 0; i < list.length; i++) {
      if (getEntryKey(list[i]) === getEntryKey(incoming)) {
        list[i] = mergeEntry(list[i], incoming);
        found = true;
        break;
      }
    }
    if (!found) {
      list.push(incoming);
    }
    return write(list);
  }

  function remove(productId) {
    var key = getEntryKey(normalize(productId) || { key: productId });
    if (key == null) return read();
    var list = read().filter(function (item) { return getEntryKey(item) !== key; });
    return write(list);
  }

  function toggle(productId, productHandle) {
    var incoming = normalize(productId) || normalize({ id: productId, handle: productHandle });
    if (!incoming) return read();
    return has(getEntryKey(incoming)) ? remove(incoming) : add(incoming, productHandle);
  }

  /* ------------------------------- DOM layer ------------------------------- */

  function syncButton(btn) {
    if (!btn) return;
    var key = btn.getAttribute('data-variant-id') || btn.getAttribute('data-product-id');
    if (!key) return;
    var active = has(key);
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.setAttribute(
      'aria-label',
      active ? 'Remove from wishlist' : 'Add to wishlist'
    );
  }

  function readButtonEntry(btn) {
    if (!btn) return null;
    var productId = btn.getAttribute('data-product-id');
    var variantId = btn.getAttribute('data-variant-id');
    var productHandle = btn.getAttribute('data-product-handle');
    var productTitle = btn.getAttribute('data-product-title');
    var variantTitle = btn.getAttribute('data-variant-title');
    var displayTitle = btn.getAttribute('data-display-title');
    var variantImage = btn.getAttribute('data-variant-image');
    var price = btn.getAttribute('data-variant-price');
    var compareAtPrice = btn.getAttribute('data-variant-compare-at-price');
    var variantOptions = parseVariantOptions(btn.getAttribute('data-variant-options'));

    if (!productId && !variantId) return null;

    return normalize({
      key: variantId || productId,
      productId: productId,
      variantId: variantId,
      handle: productHandle,
      productTitle: productTitle,
      variantTitle: variantTitle,
      title: displayTitle || variantTitle || productTitle,
      displayTitle: displayTitle,
      image: variantImage,
      variantImage: variantImage,
      price: price,
      compareAtPrice: compareAtPrice,
      variantOptions: variantOptions
    });
  }

  function syncBadges() {
    var c = count();
    var badges = document.querySelectorAll(BADGE_SELECTOR);
    suppressBadgeObserver = true;
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = c;
      if (c > 0) {
        badges[i].removeAttribute('hidden');
        badges[i].classList.add('is-visible');
      } else {
        badges[i].setAttribute('hidden', '');
        badges[i].classList.remove('is-visible');
      }
      observeBadge(badges[i]);
    }
    // Release the guard after the mutations we just made have been queued
    // for delivery to any observers (microtask timing — see observeBadge).
    Promise.resolve().then(function () { suppressBadgeObserver = false; });
  }

  /* ------------------------- badge self-healing watch ------------------------
     Some themes (this one included, via `header-actions`/section morphing on
     cart or localization updates) can patch an *existing* badge element's
     attributes/text back to their server-rendered defaults (hidden, "0")
     without removing or replacing the node itself. That kind of in-place
     patch is invisible to a childList MutationObserver, which is why the
     badge can correctly show a count and then quietly revert later. Watch
     each badge element directly for exactly that, and re-assert the real
     count the instant it happens.
     ---------------------------------------------------------------------- */

  var suppressBadgeObserver = false;
  var watchedBadges = typeof WeakSet !== 'undefined' ? new WeakSet() : null;

  function observeBadge(badge) {
    if (!badge || !('MutationObserver' in window)) return;
    if (watchedBadges) {
      if (watchedBadges.has(badge)) return;
      watchedBadges.add(badge);
    } else if (badge.dataset.wishlistWatched === 'true') {
      return;
    } else {
      badge.dataset.wishlistWatched = 'true';
    }

    var badgeObserver = new MutationObserver(function () {
      if (suppressBadgeObserver) return;
      syncBadges();
    });
    badgeObserver.observe(badge, {
      attributes: true,
      attributeFilter: ['hidden', 'class'],
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  function syncAll(scope) {
    var root = scope || document;
    var btns = root.querySelectorAll(BTN_SELECTOR);
    for (var i = 0; i < btns.length; i++) syncButton(btns[i]);
    syncBadges();
  }

  function handleClick(e) {
    e.preventDefault();
    e.stopPropagation();

    var btn = e.currentTarget;
    var entry = readButtonEntry(btn);
    if (!entry) return;

    toggle(entry);

    // Click pulse
    btn.classList.remove('is-clicked');
    void btn.offsetWidth;
    btn.classList.add('is-clicked');
    setTimeout(function () { btn.classList.remove('is-clicked'); }, 350);
  }

  function bind(btn) {
    if (btn.dataset[INIT_FLAG] === 'true') return;
    btn.dataset[INIT_FLAG] = 'true';
    btn.addEventListener('click', handleClick);
    syncButton(btn);
  }

  function bindAll(scope) {
    var root = scope || document;
    var btns = root.querySelectorAll(BTN_SELECTOR);
    for (var i = 0; i < btns.length; i++) bind(btns[i]);
  }

  /* ----------------------------- lifecycle hooks ---------------------------- */

  function migrateMissingHandles() {
    var items = read();
    var needsByItem = {};
    var anyMissing = false;

    for (var i = 0; i < items.length; i++) {
      if (!items[i].handle) {
        needsByItem[getEntryKey(items[i])] = i;
        anyMissing = true;
      }
    }
    if (!anyMissing) return;

    var btns = document.querySelectorAll(BTN_SELECTOR);
    var dirty = false;
    for (var j = 0; j < btns.length; j++) {
      var id     = btns[j].getAttribute('data-variant-id') || btns[j].getAttribute('data-product-id');
      var handle = btns[j].getAttribute('data-product-handle');
      if (id && handle && needsByItem[id] !== undefined) {
        items[needsByItem[id]].handle = handle;
        dirty = true;
      }
    }

    if (dirty) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (e) {}
    }
  }
  
  function boot() {
    migrateMissingHandles();
    bindAll();
    syncBadges();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    bindAll(e.target);
    syncBadges();
  });

  // Cross-tab sync
  window.addEventListener('storage', function (e) {
    if (e.key === STORAGE_KEY) syncAll();
  });

  // Keep all UI in sync after every change
  window.addEventListener(EVENT_CHANGE, function () { syncAll(); });

  // Catch dynamically injected buttons (quick-view, AJAX, predictive search)
  if ('MutationObserver' in window) {
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(BTN_SELECTOR)) {
            bind(node);
          } else if (node.querySelectorAll) {
            bindAll(node);
          }
          // A re-rendered header (e.g. re-fetched partial) can drop a fresh
          // copy of the badge back to its default hidden/zero state. Catch
          // that here so it doesn't silently go stale until the next click.
          if (node.querySelector && (
            node.matches(BADGE_SELECTOR) || node.querySelector(BADGE_SELECTOR)
          )) {
            syncBadges();
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  // Pages restored from the back/forward cache (bfcache) keep their old DOM
  // and JS state, which can be stale relative to what's actually in
  // localStorage (e.g. wishlist changed in another tab since the page was
  // cached). Re-sync everything whenever the page is shown this way.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) syncAll();
  });

  /* ------------------------------- expose API ------------------------------ */

  window.AmolihWishlist = {
    get:    get,
    getIds: getIds,
    has:    has,
    add:    add,
    remove: remove,
    toggle: toggle,
    count:  count,
    syncButton: syncButton,
    _sync:  syncAll
  };
})();